<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import SquaresGrid from '@/components/SquaresGrid.vue';
import { DEMO_OWNER_ID, DEMO_TEAMS, useHomeReveal } from '@/composables/useHomeReveal';
import { t } from '@/i18n';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/*
 * The example board, told as a story rather than shown as a picture — the campaign video's
 * beats on the app's own grid. The squares, the timing and the resting state all live in the
 * composable; this is the screen it plays on.
 */
const board = ref<HTMLElement | null>(null);
const { state, told } = useHomeReveal(board);

/**
 * The line over the board, in the middle of it. There is no line for the reveal itself: the
 * words clear first so that nothing is standing in front of the square the story is about.
 */
const storyline = () => {
  switch (state.value.caption) {
    case 'yours':
      return t('home.reveal.yours');
    case 'theirs':
      return t('home.reveal.theirs');
    case 'score':
      return t('home.reveal.score', { a: state.value.score?.a ?? 0, b: state.value.score?.b ?? 0 });
    default:
      return '';
  }
};
</script>

<template>
  <section>
    <h1 class="text-3xl font-semibold">{{ t('home.heading') }}</h1>

    <!--
      What the game is, in one sentence, before anything else — the board below is illustrating
      this, and a reader who meets the board first is decoding a diagram. It is also the only
      explanation a reduced-motion or screen-reader visitor gets: the storyline over the board is
      aria-hidden, and the result caption is held back until the last beat.
    -->
    <p class="mt-4 max-w-prose text-ink">{{ t('home.explain') }}</p>

    <!--
      Then the money question, and still above the join/host pair: somebody who suspects a scam
      leaves before they scroll. It comes *after* the explanation now rather than instead of it —
      28 of the 39 words above the fold used to be about price and signup, which is a page
      defending itself to a stranger who has not yet been told what it does.
    -->
    <p class="mt-4 max-w-prose font-medium text-ink">{{ t('home.free') }}</p>
    <p class="mt-1 max-w-prose text-ink-muted">{{ t('home.noAccount') }}</p>

    <!--
      Hosting is the filled one, and first. Everybody who reaches this page is a prospective
      *host*: somebody holding a code arrives on /join/:code from the link they were sent and
      never sees it. The pair was the other way round, which spent the loudest control on the
      page on the one action its only audience cannot take. The code button stays, as the
      outline — people do get read a code over a braai — but it is the second answer.

      Do not name either button's label in a comment in here: the test that pins this order
      reads element order for exactly that reason, and a sibling test still scans the HTML.
    -->
    <div class="mt-6 flex flex-wrap gap-3">
      <!--
        The ring is the board's own predicted-square frame — `.guess-frame`, one rule, shared —
        and it waits for the story below to finish telling itself. A ring that is already there
        when you arrive is decoration; landing it on the beat the result prints makes it the
        answer to the thing you have just watched. `told` rather than `state.done`, because the
        board *rests* on its finished frame before anybody has scrolled to it.
      -->
      <RouterLink
        to="/host/new"
        class="rounded bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-strong"
        :class="told && 'guess-frame'"
      >
        {{ t('home.hostGame') }}
      </RouterLink>
      <RouterLink
        to="/join"
        class="rounded border border-accent px-4 py-2 font-medium hover:bg-fill"
      >
        {{ t('home.haveCode') }}
      </RouterLink>
    </div>

    <!--
      Under the pair rather than in it: that row is a two-way decision, and a third equal-weight
      button turns a choice into a menu.
    -->
    <p class="mt-3 text-sm">
      <RouterLink to="/how-to-play" class="text-target underline text-ink-muted hover:text-ink">
        {{ t('home.howToPlay') }}
      </RouterLink>
    </p>

    <!--
      The argument for making that decision: watching a board fill up shows somebody who has
      never played this what the sentence above means, and a second paragraph would not.

      It has a heading because the page had none — one h1 and then eight hundred pixels of
      picture, with nothing to scan and nothing naming the board to a reader who cannot see it
      animate.
    -->
    <h2 class="mt-10 text-lg font-semibold">{{ t('home.boardHeading') }}</h2>

    <!-- Centred and given its head. The paragraphs above are held to a reading measure; the
         board is the picture, and the campaign video's whole trick is that it is big enough to
         watch. -->
    <div
      ref="board"
      class="relative mx-auto mt-8 max-w-3xl rounded-lg border border-line bg-surface p-4"
    >
      <!-- Says what the finished board in front of you means, and held back while the story is
           telling itself: printed from the first frame it gives the ending away. The teams are
           not in it — they are on the board's own two bars, the way a real game labels them. -->
      <p class="mb-3 min-h-[1.25rem] text-sm font-medium text-ink-muted">
        {{ state.done ? t('home.exampleResult') : '' }}
      </p>

      <SquaresGrid
        :squares="state.squares"
        :team-a="DEMO_TEAMS.a"
        :team-b="DEMO_TEAMS.b"
        :team-a-colour="DEMO_TEAMS.aColour"
        :team-b-colour="DEMO_TEAMS.bColour"
        :column-digits="DIGITS"
        :row-digits="DIGITS"
        :owner-id="DEMO_OWNER_ID"
        compact
        :winning="state.winning"
      />

      <!--
        The story, over the board it is describing.

        `aria-hidden` because it is a line of text replaced four times in seven seconds, narrating
        a decorative grid — noise to a screen reader. The caption above says the same thing once,
        and stays.
      -->
      <!-- `out-in`: the two pills sit in the same place, so a cross-fade is one line of text
           printed over another for a fifth of a second. -->
      <Transition name="storyline" mode="out-in">
        <p
          v-if="state.caption"
          :key="state.caption"
          data-storyboard
          aria-hidden="true"
          class="storyline"
        >
          {{ storyline() }}
        </p>
      </Transition>
    </div>

    <!--
      Something to press where the story leaves the eye.

      The last interactive thing on this page used to be the "how it works" link a third of the
      way down, and everything below it was reading — so a reader the animation had just convinced
      had to scroll back past all of it. One button, not the pair again: the choice was made at the
      top, and this is the same decision arriving at the moment it has been argued for.

      `DigitOddsChart` used to close the page and is now on /how-to-play, under the question it
      answers. It is post-decision content — sampling error and joint probability — and it was
      half the words on a page whose reader does not yet know what a square is.
    -->
    <h2 class="mt-10 text-lg font-semibold">{{ t('home.closingHeading') }}</h2>
    <div class="mt-4">
      <RouterLink
        to="/host/new"
        class="inline-block rounded bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-strong"
      >
        {{ t('home.hostGame') }}
      </RouterLink>
    </div>
  </section>
</template>
